"use client";

import { useEffect, useRef, useState, Suspense } from "react";
import { useParams, useSearchParams } from "next/navigation";
import { FormConfigType } from "@/utils/types/store";
import FormPreview from "@/app/(dashboard)/dashboard/form-builder/[formId]/_components/FormPreview";
import TurnstileWidget, { executeTurnstile } from "@/app/_components/TurnstileWidget";
import type { TurnstileInstance } from "@marsidev/react-turnstile";

const FormPageContent = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const formId = params.formId as string;
  const className = searchParams.get("className");
  const bgColor = searchParams.get("bgColor");
  const textColor = searchParams.get("textColor");

  const [formData, setFormData] = useState<FormConfigType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [country, setCountry] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then((res) => res.json())
      .then((data) => {
        setCountry(data.country_name);
      })
      .catch((err) => {
        console.error("Error fetching country:", err);
      });

    if (formId) {
      setLoading(true);
      setError(null);
      fetchFormData(formId)
        .then((data) => {
          if (data) {
            if (className || bgColor || textColor) {
              data.formStyles = {
                ...data.formStyles,
                FieldLabel: {
                  color: textColor || data.formStyles.FieldLabel.color,
                },
                Button: {
                  ...data.formStyles.Button,
                  backgroundColor:
                    bgColor || data.formStyles.Button.backgroundColor,
                },
              };
              data.header.className = className || data.header.className;
            }
            setFormData(data);
          } else {
            setError("Form not found. Please check the form ID and try again.");
          }
        })
        .catch((err) => {
          setError("Unable to load the form. Please try again later.");
          console.error("Error fetching form:", err);
        })
        .finally(() => setLoading(false));
    }
  }, [formId, className, bgColor, textColor]);

  const handleSubmit = async (payload: {
    email: string;
    mailchimpKey: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    country?: string;
  }): Promise<boolean> => {
    try {
      // Reset states
      setSubmitError(null);
      setSubmitSuccess(false);

      // Form validation
      if (!payload.email) {
        setSubmitError("Email is required.");
        return false;
      }

      if (!payload.mailchimpKey) {
        setSubmitError("Mailchimp key is required.");
        return false;
      }

      const turnstileToken = await executeTurnstile(turnstileRef);
      if (!turnstileToken) {
        setSubmitError(
          "Verification failed. Please disable any ad blockers and try again.",
        );
        return false;
      }

      const { email, mailchimpKey } = payload;

      const response = await fetch("/api/forms/submit-and-fetch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          mailchimpKey,
          formId,
          firstName: payload.firstName,
          lastName: payload.lastName,
          phone: payload.phone,
          country,
          turnstileToken,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage =
            errorData.error ||
            errorData.message ||
            "Form submission failed. Please try again.";
        } catch {
          errorMessage = "Form submission failed. Please try again.";
        }

        setSubmitError(errorMessage);
        return false;
      }

      // Success case
      setSubmittedEmail(payload.email);
      setSubmitSuccess(true);
      return true;
    } catch (error: any) {
      console.error("Error submitting form:", error);
      setSubmitError("An unexpected error occurred. Please try again.");
      return false;
    } finally {
      turnstileRef.current?.reset();
    }
  };

  const handleCloseSuccess = () => {
    setSubmitSuccess(false);
    setSubmittedEmail("");
  };

  const handleCloseError = () => {
    setSubmitError(null);
  };

  if (loading) {
    return (
      <div className="flex size-full items-center justify-center p-4">
        <div className="text-center">
          <div className="mx-auto mb-2 size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          <p className="text-sm">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex size-full items-center justify-center p-4">
        <div className="w-full rounded border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="flex size-full items-center justify-center p-4">
        <div className="w-full rounded border border-yellow-200 bg-yellow-50 p-4">
          <p className="text-sm text-yellow-700">
            Form not found. Please check the form ID.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex size-full items-center justify-center p-4">
      <div className="w-full">
        <div className="mb-4 flex justify-center">
          <TurnstileWidget ref={turnstileRef} />
        </div>
        <FormPreview
          formPreviewData={formData}
          handleSubmit={handleSubmit}
          submitSuccess={submitSuccess}
          submitError={submitError}
          onCloseSuccess={handleCloseSuccess}
          onCloseError={handleCloseError}
          submittedEmail={submittedEmail}
        />
      </div>
    </div>
  );
};

const fetchFormData = async (
  formId: string,
): Promise<FormConfigType | null> => {
  try {
    const response = await fetch(`/api/forms/${formId}`);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching form data:", error);
    throw error;
  }
};

const FormPage = () => {
  return (
    <Suspense fallback={
      <div className="flex size-full items-center justify-center p-4">
        <div className="text-center">
          <div className="mx-auto mb-2 size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          <p className="text-sm">Loading...</p>
        </div>
      </div>
    }>
      <FormPageContent />
    </Suspense>
  );
};

export default FormPage;
